import React, { useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/incentive-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import { QueryParams } from 'src/types/grid';
import { IIncentive } from 'src/types/incentive';
import IncentivesActionsColumn from './IncentivesActionsColumn';
import textFilter from '../../../../../../../components/react-table/filters/text-filter';
import { CellProps } from 'react-table';
import { Link } from 'react-router-dom';
import Router from '../../../../../../../navigation/router';
import { routes } from '../../../../../../../navigation';
import dropdownFilter from '../../../../../../../components/react-table/filters/dropdown-filter';
import dateFilterMulti from '../../../../../../../components/react-table/filters/date-filter-multi';
import dateFilter from '../../../../../../../components/react-table/filters/date-filter';
import { changeToZeroMoment } from '../../../../../../../helpers/timeHelpers';
import { numberWithCommas } from '../../../../../../../helpers/format';

interface Props {
    data: Paginated<IIncentive>;
    providers: SelectObject[];
    getData: (params: QueryParams) => void;
    getDataCsv: (params: QueryParams) => void;
    getDataInFile: (params: QueryParams) => void;
}

const getColumns = (
    dataProd: SelectObject[],
    updateState: boolean,
    setUpdateState: (parm: boolean) => void,
): Column<IIncentive>[] => [
    {
        Header: 'Received Bonus ID',
        accessor: 'id',
        disableSortBy: true,
        Filter: textFilter<IIncentive>(),
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => (
            <Link
                className={'mr-1'}
                to={Router.generate(routes.PROVIDER_DETAIL, {
                    id: original?.provider?.id,
                    tab: 'received-bonuses-detail',
                    subId: original.id,
                })}
            >
                {original.id}
            </Link>
        ),
    },

    {
        Header: 'Provider',
        accessor: 'provider.id',
        disableSortBy: true,
        Filter: dropdownFilter<IIncentive>(dataProd),
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => {
            if (!original?.provider) return '';

            return (
                <Link
                    to={Router.generate(routes.PROVIDER_DETAIL, {
                        id: original?.provider?.id,
                        tab: 'main-info',
                        subId: '',
                    })}
                >
                    {original.provider?.fullName}
                </Link>
            );
        },
    },
    {
        Header: 'Employee ID',
        accessor: 'provider.employeeId',
        disableSortBy: true,
        Filter: textFilter<IIncentive>(),
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => {
            if (!original?.provider) return '';
            return original.provider?.employeeId;
        },
    },
    {
        Header: 'Received Date',
        accessor: 'incentiveDate',
        disableSortBy: true,
        headerClassName: 'text-center w-100 d-block',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => {
            return (
                <div className={'text-center'}>
                    {original.incentiveDate ? changeToZeroMoment(original.incentiveDate, 'MMM D, YYYY') : ''}
                </div>
            );
        },
        Filter: dateFilterMulti<IIncentive>(),
    },
    {
        Header: 'Total',
        accessor: 'total',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => {
            return original.total ? `$${numberWithCommas(original.total)}` : '$0';
        },
        Filter: textFilter<IIncentive>(),
    },
    {
        Header: 'Period Start',
        accessor: 'periodStart[after]',
        disableSortBy: true,
        Filter: dateFilter<IIncentive>(),
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => changeToZeroMoment(original.periodStart, 'MMM D, YYYY'),
    },
    {
        Header: 'Period End',
        accessor: 'periodEnd[before]',
        disableSortBy: true,
        Filter: dateFilter<IIncentive>(),
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IIncentive>) => changeToZeroMoment(original.periodEnd, 'MMM D, YYYY'),
    },
    {
        Header: 'Actions',
        accessor: 'createdAt',
        Cell: (pr) => <IncentivesActionsColumn {...pr} updateState={updateState} setUpdateState={setUpdateState} />,
        disableSortBy: true,
        disableFilters: true,
        width: '20%',
    },
];

const IncentivesGrid: React.FunctionComponent<Props> = ({
    data,
    getData,
    getDataCsv,
    getDataInFile,
    providers,
}: Props) => {
    const [columns, setColumns] = useState<any>([]);
    const [updateState, setUpdateState] = useState<any>(false);

    if (providers.length && !columns.length) {
        setColumns(getColumns(providers, updateState, setUpdateState));
    }

    return (
        <div className={classNames(styles.grid)}>
            <Grid<IIncentive>
                getDataCsv={getDataCsv}
                getDataInFile={getDataInFile}
                columns={columns}
                data={data}
                getData={getData}
            />
        </div>
    );
};

export default IncentivesGrid;
